import { useCallback, useLayoutEffect, useRef, useState, useMemo } from 'react';

import debounce from 'lodash/debounce';

const useAutoHeight = () => {
  const [height, setHeight] = useState(500);
  const container = useRef();

  const resizeContainer = useCallback(() => {
    if (container.current) {
      setHeight(window.innerHeight - container.current.offsetTop);
    }
  }, []);

  const debouncedResize = useMemo(() => debounce(resizeContainer, 1000), [resizeContainer]);

  const handleResize = useCallback(() => debouncedResize(), [debouncedResize]);

  const setupMutationObserver = useCallback(() => {
    if (!MutationObserver || !container.current?.parentNode) {
      return () => {};
    }

    const observer = new MutationObserver(resizeContainer);

    observer.observe(container.current.parentNode, { childList: true });

    return () => observer.disconnect();
  }, [resizeContainer]);

  useLayoutEffect(() => {
    if (container.current && window) {
      resizeContainer();
    }
  }, [resizeContainer]);

  useLayoutEffect(() => {
    const cleanupObserver = setupMutationObserver();

    return cleanupObserver;
  }, [setupMutationObserver]);

  useLayoutEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      debouncedResize.cancel();
    };
  }, [handleResize, debouncedResize]);

  return {
    height,
    containerRef: container,
  };
};

export default useAutoHeight;
